import { Chip, CircularProgress, IconButton } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { useContext, useEffect, useState } from 'react'
import CustomInput from '../TextInput'
import UtilitiesService from '../../services/UtilitiesService'
import CustomCheckbox from '../Checkbox'
import CustomSwitch from '../Switch'
import { PageContext } from '../../context/PageContext'

interface Props {
    text: string
    handleClose: () => void
    selectedAbilities: string[]
    setSelectedAbility: (ability: string) => void
}
const SearchAbilitiesModal = ({
    text,
    selectedAbilities,
    setSelectedAbility,
    handleClose,
}: Props) => {
    const [abilities, setAbilities] = useState<string[] | null>()
    const [filter, setFilter] = useState<string>('')
    const [startsWith, setStartsWith] = useState<boolean>(false)
    const { smallScreen } = useContext(PageContext)

    useEffect(() => {
        if (!filter) {
            setAbilities(undefined)
            return
        }

        setAbilities(null)
        const timeout = setTimeout(() => {
            new UtilitiesService().getAbilities(filter, startsWith).then((response) => {
                if (response.status !== 200) return
                setAbilities(response.data)
            })
        }, 1500)

        return () => clearTimeout(timeout)
    }, [filter, startsWith])

    const abilityDisplay = (ability: string) => {
        return (
            <div className="flex gap-2 items-center">
                <CustomCheckbox
                    checked={selectedAbilities.includes(ability)}
                    onChange={(e) => setSelectedAbility(ability)}
                />
                <span>{ability}</span>
            </div>
        )
    }

    return (
        <div className="flex flex-col w-[50vw] md:w-[40vw] max-h-[50vh] md:max-h-[72vh] gap-1 md:gap-2 md:p-2">
            <div className="flex items-center">
                <label className="text-secondary font-semibold text-sm md:text-base">{text}</label>
                <IconButton
                    onClick={handleClose}
                    className="ml-auto p-0 text-primary"
                >
                    <ClearIcon />
                </IconButton>
            </div>
            <div
                className={`flex gap-1 items-center ${selectedAbilities.length > 0 ? 'my-2 md:my-4' : ''} flex-wrap`}
            >
                {selectedAbilities.map((ability) => (
                    <div
                        key={`selected${ability}`}
                        className="flex w-full"
                    >
                        <Chip
                            label={ability}
                            onDelete={() => setSelectedAbility(ability)}
                            className="bg-primary text-white"
                            sx={{
                                '& .MuiChip-deleteIcon': {
                                    color: 'white',
                                },
                                '&:hover .MuiChip-deleteIcon': {
                                    color: 'white',
                                },
                            }}
                        />
                    </div>
                ))}
            </div>
            <div className="flex flex-col md:flex-row gap-2 w-full items-center">
                {smallScreen && (
                    <div className="flex items-center justify-start w-full">
                        <CustomSwitch
                            value={startsWith}
                            onChange={(e) => setStartsWith(e.target.checked)}
                        />
                        <span className="whitespace-nowrap text-sm">Búsqueda textual</span>
                    </div>
                )}
                <CustomInput
                    type="text"
                    name="filter"
                    value={filter}
                    label="Buscar"
                    placeholder="Se mostrarán los primeros 10 resultados"
                    onChange={(event) => setFilter(event.target.value)}
                    className="w-full mr-2"
                />
                {!smallScreen && (
                    <div className="flex items-center">
                        <CustomSwitch
                            value={startsWith}
                            onChange={(e) => setStartsWith(e.target.checked)}
                        />
                        <span className="whitespace-nowrap">Búsqueda textual</span>
                    </div>
                )}
            </div>
            {!!abilities ? (
                abilities.length > 0 ? (
                    <div className="flex flex-col gap-1 md:gap-0">
                        {abilities.map((ability) => (
                            <div key={`filtered${ability}`}>{abilityDisplay(ability)}</div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col gap-2">
                        <div className="flex gap-2 items-center">
                            <CustomCheckbox
                                checked={selectedAbilities.includes(filter)}
                                onChange={(e) => setSelectedAbility(filter)}
                            />
                            <span>{`Agregar "${filter}"`}</span>
                        </div>
                        <span className="text-secondary text-sm">
                            <b>Aclaración:</b> Por favor, únicamente agregá una aptitud nueva si
                            estás seguro de que no existe y si lo creés completamente necesario.
                        </span>
                    </div>
                )
            ) : (
                abilities === null && (
                    <div className="flex w-full justify-center">
                        <CircularProgress
                            className="text-primary md:ml-2 mb-2 md:mb-3 mt-3 md:mt-6"
                            size={36}
                        />
                    </div>
                )
            )}
        </div>
    )
}

export default SearchAbilitiesModal
